import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import FinalCTA from "@/sections/FinalCTA";
import { ArrowUpRight, Flame } from "lucide-react";
import { featured, posts } from "@/lib/resources";

export default function ResourcesPage() {
 return (
 <div data-testid="page-resources">
 <Seo
  title="Founder Guides: Structure, Compliance & Launch"
  description="Plain-English guides on Pvt Ltd vs LLP vs OPC, DPIIT recognition, first 90 days, cap tables and GST for first-time Indian founders."
  path="/resources"
  breadcrumbs={[
    { name: "Home", path: "/" },
    { name: "Resources", path: "/resources" },
  ]}
 />
 <PageHero
 eyebrow="Resources"
 title="Reads worth your First-Time Founder attention."
 subtitle="No fluff. Just plain-English guides on structure, compliance and launch."
 rightSlot={
 <div className="relative">
 <div className="absolute -top-5 -right-5 w-32 h-32 rounded-full bg-[#FDD805] opacity-90 -z-0" aria-hidden />
 <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-2xl border-2 border-[#0F1115] -z-0" aria-hidden />
 <div className="relative bg-white border border-[#EAEAEA] rounded-3xl p-5 shadow-[0_10px_40px_rgba(0,0,0,0.05)] z-10" data-testid="resources-hero-toplist">
 <div className="flex items-center justify-between px-1">
 <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
 Most read this month
 </div>
 <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-[#0F1115] bg-[#FEF6C7] px-2 py-0.5 rounded-full">
 <Flame size={10} className="text-[#0F1115]" /> Trending
 </div>
 </div>
 <div className="mt-3 divide-y divide-[#EAEAEA]">
 {posts.slice(0, 3).map((p, idx) => (
 <Link
 key={p.slug}
 to={`/resources/${p.slug}`}
 className="group flex items-center gap-3 py-3 first:pt-2 last:pb-1"
 data-testid={`resources-hero-toplist-${p.slug}`}
 >
 <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-[#EAEAEA]">
 <img
 src={p.img}
 alt=""
 className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
 />
 </div>
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2">
 <div className="font-display font-black text-[13px] text-[#4A4A4A] tabular-nums">
 0{idx + 1}
 </div>
 <div className="inline-block text-[9px] uppercase tracking-[0.2em] font-bold text-[#0F1115] bg-[#FDD805] px-2 py-0.5 rounded-full">
 {p.tag}
 </div>
 </div>
 <div className="mt-1 text-[13px] font-bold text-[#0F1115] leading-tight line-clamp-2 group-hover:underline decoration-[#FDD805] decoration-2 underline-offset-2">
 {p.title}
 </div>
 <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#4A4A4A] mt-1">
 {p.read} read
 </div>
 </div>
 </Link>
 ))}
 </div>
 </div>
 </div>
 }
 />

 <section className="bg-white">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16">
 <Reveal>
 <Link
 to={`/resources/${featured.slug}`}
 className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#F8F9FA] border border-[#EAEAEA] rounded-3xl overflow-hidden hover:border-[#0F1115] transition-colors"
 data-testid="resources-featured-card"
 >
 <div className="lg:col-span-6">
 <img src={featured.img} alt="" className="w-full h-full object-cover min-h-[280px] group-hover:scale-105 transition-transform duration-700" />
 </div>
 <div className="lg:col-span-6 p-8 lg:p-12">
 <div className="inline-block text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F1115] bg-[#FDD805] px-2.5 py-1 rounded-full">
 Featured · {featured.tag}
 </div>
 <h2 className="mt-4 font-display text-[28px] md:text-[36px] leading-[1.1] font-black text-[#0F1115]">
 {featured.title}
 </h2>
 <div className="mt-4 text-[12px] text-[#4A4A4A]">{featured.read}</div>
 <div className="mt-6 inline-flex items-center gap-2 text-[14px] font-bold text-[#0F1115] link-underline w-fit" data-testid="resources-featured-cta">
 Read the guide <ArrowUpRight size={14} />
 </div>
 </div>
 </Link>
 </Reveal>
 </div>

 <div className="max-w-[1280px] mx-auto px-5 md:px-8 pb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" id="all-posts">
 {posts.map((p, i) => (
 <Reveal key={p.slug} delay={i * 60}>
 <Link
 to={`/resources/${p.slug}`}
 className="group block h-full bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden hover:border-[#0F1115] transition-colors"
 data-testid={`resource-page-card-${p.slug}`}
 >
 <div className="aspect-[16/10] overflow-hidden">
 <img src={p.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
 </div>
 <div className="p-6">
 <div className="inline-block text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F1115] bg-[#FFF9E6] px-2.5 py-1 rounded-full">
 {p.tag}
 </div>
 <h3 className="mt-4 font-display text-[19px] font-bold text-[#0F1115] leading-[1.25]">
 {p.title}
 </h3>
 <div className="mt-4 text-[12px] text-[#4A4A4A]">{p.read} read</div>
 </div>
 </Link>
 </Reveal>
 ))}
 </div>
 </section>

 <FinalCTA />
 </div>
 );
}
